import React, {useContext, useEffect, useState} from 'react';
import {Form, Input, Select} from 'antd';
import GenericDialog from '@common/GenericDialog';
import Alert from '@common/Alert';
import axios from '@common/axios';
import {UserContext} from '@context/UserContext';

const DuplicateScheduleDialog = ({visible, handleClose, onSuccess, plan}) => {
    const {user} = useContext(UserContext);
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const currentYear = new Date().getFullYear();
    const startYear = Math.min(currentYear, 2026);
    const yearOptions = Array.from({length: currentYear + 5 - startYear + 1}, (_, i) => ({
        label: startYear + i,
        value: startYear + i,
    }));

    const classOptions = (user?.classes || []).map(c => ({
        label: c.className,
        value: c.classId,
    }));

    useEffect(() => {
        if (visible && plan) {
            const validClassIds = (plan.classIds || []).filter(id =>
                classOptions.some(opt => opt.value === id)
            );
            form.setFieldsValue({
                name: plan.name,
                description: plan.description || undefined,
                year: plan.year || undefined,
                classIds: validClassIds,
            });
            setErrorMessage(null);
        }
        if (!visible) {
            form.resetFields();
            setErrorMessage(null);
        }
    }, [visible, plan, form, classOptions]);

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await axios.post(`/web/ui/lessonScheduler/plans/${plan.scheduleId}/duplicate`, {
                name: values.name,
                description: values.description || null,
                year: values.year || null,
                syllabusId: user?.syllabusId || null,
                classIds: values.classIds?.length ? values.classIds : null,
            });
            onSuccess(response.data);
            handleClose();
        } catch (e) {
            const msg = e?.response?.data?.message || 'Failed to duplicate schedule.';
            setErrorMessage(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <GenericDialog
            title="Duplicate Schedule"
            open={visible}
            onClose={handleClose}
            isLoading={isLoading}
            errorMessage={errorMessage}
            dialogWidth={480}
            buttons={[
                {name: 'Cancel', extraProps: {type: 'text'}},
                {name: 'Duplicate', callback: handleSubmit, extraProps: {type: 'primary'}},
            ]}
        >
            <Alert
                type="info"
                message="Your duplicate will include all units, lessons, and concepts from the original. Update the details below as needed."
                style={{marginBottom: 16}}
            />
            <Form form={form} layout="vertical" className="ch-form">
                <Form.Item name="name" label="Name" rules={[{required: true, message: 'Name is required'}]}>
                    <Input placeholder="e.g. Year 10 Term 1" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea rows={3} placeholder="Optional description" />
                </Form.Item>
                <Form.Item name="year" label="Year">
                    <Select options={yearOptions} placeholder="Select a year (optional)" allowClear />
                </Form.Item>
                <Form.Item name="classIds" label="Classes">
                    <Select options={classOptions} placeholder="Select classes (optional)" allowClear mode="multiple" />
                </Form.Item>
            </Form>
        </GenericDialog>
    );
};

export default DuplicateScheduleDialog;
